import { z } from "zod";

/** Minimum password length surfaced to the client; WorkOS enforces the full policy server-side. */
export const PASSWORD_MIN_LENGTH = 8;

/**
 * Social providers we expose as native buttons. `authkit` falls back to the hosted picker.
 * Mobile social sign-in still requires the system auth session for the OAuth handshake.
 */
export const SOCIAL_AUTH_PROVIDERS = ["GoogleOAuth", "AppleOAuth"] as const;
export const AUTH_PROVIDERS = ["authkit", ...SOCIAL_AUTH_PROVIDERS] as const;

export type SocialAuthProvider = (typeof SOCIAL_AUTH_PROVIDERS)[number];
export type AuthProvider = (typeof AUTH_PROVIDERS)[number];

export const AuthorizationUrlSchema = z.object({
  codeChallenge: z.string().min(1),
  codeChallengeMethod: z.literal("S256"),
  redirectUri: z.string().url(),
  state: z.string().optional(),
  provider: z.enum(AUTH_PROVIDERS).optional(),
});

export type AuthorizationUrlInput = z.infer<typeof AuthorizationUrlSchema>;

export const AuthCallbackSchema = z.object({
  code: z.string().min(1),
  codeVerifier: z.string().min(1),
});

export type AuthCallbackInput = z.infer<typeof AuthCallbackSchema>;

export const RefreshTokenSchema = z.object({
  refreshToken: z.string().min(1),
});

export type RefreshTokenInput = z.infer<typeof RefreshTokenSchema>;

export const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

export type LoginInput = z.infer<typeof LoginSchema>;

export const SignUpSchema = z.object({
  email: z.string().email(),
  password: z.string().min(PASSWORD_MIN_LENGTH),
  name: z.string().trim().min(1).max(100).optional(),
});

export type SignUpInput = z.infer<typeof SignUpSchema>;

export const VerifyEmailSchema = z.object({
  pendingAuthenticationToken: z.string().min(1),
  code: z.string().trim().min(1).max(12),
});

export type VerifyEmailInput = z.infer<typeof VerifyEmailSchema>;

export const AuthSessionUserSchema = z.object({
  workosId: z.string(),
  email: z.string().email(),
  name: z.string().nullable(),
  avatarUrl: z.string().url().nullable(),
});

export type AuthSessionUser = z.infer<typeof AuthSessionUserSchema>;

/** Discriminated outcome shared by every credential-based auth endpoint. */
export type AuthResult =
  | {
      status: "authenticated";
      accessToken: string;
      refreshToken: string;
      user: AuthSessionUser;
    }
  | {
      status: "verification_required";
      pendingAuthenticationToken: string;
      email: string;
    };
